import React from 'react';
import { applyTypography, useT } from '@repo/common';

const MVPSection = ({ mvps = [], description }) => {
  const t = useT();
  const displayDescription =
    description ??
    t(
      'Congratulations to our amazing Koready MVPs!\nThese outstanding participants took first place with top scores through active and dedicated participation.\nNow it’s your turn!',
      '멋진 Koready MVP 여러분, 축하합니다!\n적극적이고 성실한 참여로 최고 점수를 받아 1위를 차지한 참가자들입니다.\n이제 여러분의 차례예요!',
    );
  const resolveAvatar = (avatar) =>
    typeof avatar === 'string' && avatar.trim().length > 0
      ? avatar
      : profileImage;

  const getInitial = (name) => {
    if (!name || name.trim().length === 0) return '';
    const trimmedName = name.trim();
    // 한글인지 확인
    const isKorean = /[가-힣]/.test(trimmedName[0]);
    if (isKorean) {
      return trimmedName[0];
    }
    // 영문인 경우 첫 글자
    return trimmedName[0].toUpperCase();
  };

  const catImage = `${process.env.REACT_APP_ASSETS_URL}/img-cat-crown-face.svg`;
  const profileImage = `${process.env.REACT_APP_ASSETS_URL}/img-profile.svg`;

  const renderSmallAvatar = (mvp, size = 50) => {
    const hasImage =
      mvp.avatar &&
      typeof mvp.avatar === 'string' &&
      mvp.avatar.trim().length > 0 &&
      !mvp.avatar.includes('img-profile.svg');
    const initial = getInitial(mvp.name || 'User Name');

    if (hasImage) {
      // 프로필 이미지가 있는 경우
      return (
        <img
          alt=''
          className='absolute inset-0 max-w-none object-center object-cover pointer-events-none rounded-[50px] size-full'
          src={mvp.avatar}
        />
      );
    } else if (initial) {
      // 이니셜이 있는 경우 (검은 배경에 흰색 텍스트)
      return (
        <div className='absolute inset-0 flex items-center justify-center bg-grayscale-gray09 rounded-[50px]'>
          <p
            className={`${applyTypography('b12Strong')} text-text-inverse`}
            style={{ lineHeight: '1.4' }}
          >
            {initial}
          </p>
        </div>
      );
    } else {
      // 플레이스홀더 아이콘 (밝은 회색 배경)
      return (
        <div className='absolute inset-0 flex items-center justify-center bg-grayscale-gray02 rounded-[50px]'>
          <img
            alt=''
            className='w-[60%] h-[60%] object-contain opacity-50'
            src={profileImage}
          />
        </div>
      );
    }
  };

  if (!mvps.length) return null;

  const rawMvps = mvps;
  const hasHighlighted = rawMvps.some((mvp) => Boolean(mvp?.isHighlighted));
  const displayMvps = rawMvps.map((mvp, index) => ({
    ...mvp,
    isHighlighted: hasHighlighted ? Boolean(mvp?.isHighlighted) : index === 1,
  }));
  const visibleMvps = displayMvps.slice(0, 9);
  const mobileMvps = displayMvps.slice(0, 3);
  const mobileTopMvps = mobileMvps.slice(0, 2);
  const mobileBottomMvp = mobileMvps[2] ?? null;
  const previewMvps = displayMvps.slice(0, 2);
  const remainingCount = Math.max(displayMvps.length - previewMvps.length, 0);

  return (
    <div className='flex flex-col items-start pb-[30px] pt-0 px-0 relative shrink-0 w-full tabletH:pb-[60px]'>
      <div className='bg-grayscale-gray00 flex flex-col items-start px-[24px] py-[30px] relative shadow-3 shrink-0 w-full tabletH:rounded-[50px] tabletH:pb-[80px] tabletH:pt-[60px] tabletH:px-[80px]'>
        {/* Header */}
        <div className='flex items-start gap-[12px] pb-[24px] pt-0 px-0 relative shrink-0 w-full tabletH:items-center tabletH:gap-0 tabletH:pb-[30px]'>
          <div className='h-[53px] relative shrink-0 w-[51px] tabletH:h-[70px] tabletH:w-[68px]'>
            <div className='absolute inset-0 overflow-hidden pointer-events-none'>
              <div className='absolute h-full left-[1.13%] top-0 w-[97.74%]' />
              <img
                alt='crowncat'
                className='absolute inset-0 object-contain max-w-none size-full'
                src={catImage}
              />
            </div>
          </div>
          <div className='flex flex-1 flex-col gap-[2px] items-start min-h-px min-w-px not-italic relative shrink-0 tabletH:gap-[6px]'>
            <p
              className={`${applyTypography('b18Strong')} tabletH:${applyTypography(
                'title1',
              )} text-text-primary shrink-0 leading-[1.5] tabletH:leading-[1.2]`}
            >
              MVP
            </p>
            <p
              className={`${applyTypography('b12')} tabletH:${applyTypography(
                'b14',
              )} text-text-placeholder min-w-full shrink-0 w-[min-content] whitespace-pre-wrap`}
            >
              {displayDescription}
            </p>
          </div>
        </div>

        {/* MVP List - Mobile */}
        <div className='flex flex-col gap-[16px] items-start justify-center relative shrink-0 w-full tabletH:hidden'>
          <div className='flex gap-[8px] items-center relative shrink-0 w-full'>
            {mobileTopMvps.map((mvp) => (
              <div
                key={mvp.id}
                className={`flex flex-[1_0_0] flex-col gap-[8px] h-[160px] items-center justify-center min-h-px min-w-px overflow-clip py-[16px] relative rounded-[20px] ${mvp.isHighlighted ? 'bg-green-light01' : ''}`}
              >
                <div className='relative rounded-[50px] shrink-0 size-[80px]'>
                  <img
                    alt=''
                    className='absolute inset-0 max-w-none object-center object-cover pointer-events-none rounded-[50px] size-full'
                    src={resolveAvatar(mvp.avatar)}
                  />
                </div>
                <div className='flex flex-col font-medium items-center leading-[1.4] not-italic relative shrink-0 text-center w-full whitespace-pre-wrap'>
                  <p
                    className={`${applyTypography('b14Strong')} text-text-primary shrink-0 w-full`}
                    style={{ lineHeight: '1.4' }}
                  >
                    {mvp.name}
                  </p>
                  <p
                    className={`${applyTypography('b12Strong')} text-text-secondary shrink-0 w-full`}
                    style={{ lineHeight: '1.4' }}
                  >
                    {mvp.title}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className='flex gap-[8px] h-[160px] items-center relative shrink-0 w-full'>
            {mobileBottomMvp ? (
              <div
                className={`flex flex-[1_0_0] flex-col gap-[8px] h-[160px] items-center justify-center min-h-px min-w-px overflow-clip py-[16px] relative rounded-[20px] ${mobileBottomMvp.isHighlighted ? 'bg-green-light01' : ''}`}
              >
                <div className='relative rounded-[50px] shrink-0 size-[80px]'>
                  <img
                    alt=''
                    className='absolute inset-0 max-w-none object-center object-cover pointer-events-none rounded-[50px] size-full'
                    src={resolveAvatar(mobileBottomMvp.avatar)}
                  />
                </div>
                <div className='flex flex-col font-medium items-center leading-[1.4] not-italic relative shrink-0 text-center w-full whitespace-pre-wrap'>
                  <p
                    className={`${applyTypography('b14Strong')} text-text-primary shrink-0 w-full`}
                    style={{ lineHeight: '1.4' }}
                  >
                    {mobileBottomMvp.name}
                  </p>
                  <p
                    className={`${applyTypography('b12Strong')} text-text-secondary shrink-0 w-full`}
                    style={{ lineHeight: '1.4' }}
                  >
                    {mobileBottomMvp.title}
                  </p>
                </div>
              </div>
            ) : (
              <div className='flex-[1_0_0] h-[160px] min-h-px min-w-px' />
            )}

            <div className='flex flex-[1_0_0] flex-col gap-[12px] h-[160px] items-center justify-center min-h-px min-w-px p-[20px] relative rounded-[20px]'>
              <div className='flex items-center justify-center pl-0 pr-[10px] py-0 relative shrink-0'>
                {previewMvps.map((mvp) => (
                  <div
                    key={mvp.id}
                    className='border-[3px] border-grayscale-gray00 border-solid mr-[-10px] relative rounded-[50px] shrink-0 size-[50px]'
                  >
                    {renderSmallAvatar(mvp, 50)}
                  </div>
                ))}
                {remainingCount > 0 && (
                  <div className='bg-green-deep01 border-[3px] border-grayscale-gray00 border-solid flex flex-col items-center justify-center mr-[-10px] overflow-clip p-[10px] relative rounded-[50px] shrink-0 size-[50px]'>
                    <p
                      className={`${applyTypography('b12Strong')} text-text-inverse shrink-0 text-center w-full whitespace-pre-wrap`}
                      style={{ lineHeight: '1.4' }}
                    >
                      +{remainingCount}
                    </p>
                  </div>
                )}
              </div>
              <p
                className={`${applyTypography('b13Strong')} text-text-accent shrink-0 underline`}
                style={{ lineHeight: '1.4', textUnderlineOffset: '14%' }}
              >
                {t('See all mvp', 'MVP 전체 보기')}
              </p>
            </div>
          </div>
        </div>

        {/* MVP List - Tablet/Desktop */}
        <div className='hidden tabletH:flex relative items-center w-full gap-0 shrink-0'>
          <div className='flex flex-1 gap-[16px] items-center min-h-px min-w-px relative shrink-0 flex-nowrap'>
            {visibleMvps.map((mvp) => (
              <div
                key={mvp.id}
                className={`flex flex-col gap-[8px] h-[160px] items-center justify-center overflow-clip px-0 py-[16px] relative rounded-[20px] shrink-0 w-[140px] hover:bg-green-light01 duration-300 ${mvp.isHighlighted ? 'bg-green-light01' : ''}`}
              >
                <div className='relative rounded-[50px] shrink-0 size-[80px]'>
                  <img
                    alt=''
                    className='absolute inset-0 max-w-none object-center object-cover pointer-events-none rounded-[50px] size-full'
                    src={resolveAvatar(mvp.avatar)}
                  />
                </div>
                <div className='flex flex-col font-medium items-center leading-[1.4] not-italic relative shrink-0 text-center w-full whitespace-pre-wrap'>
                  <p
                    className={`${applyTypography('b14Strong')} text-text-primary shrink-0 w-full`}
                    style={{ lineHeight: '1.4' }}
                  >
                    {mvp.name}
                  </p>
                  <p
                    className={`${applyTypography('b12Strong')} text-text-secondary shrink-0 w-full`}
                    style={{ lineHeight: '1.4' }}
                  >
                    {mvp.title}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div className='flex flex-col items-end relative shrink-0 w-[280px]'>
            <div className='flex flex-col gap-[12px] h-[160px] items-center justify-center p-[20px] relative rounded-[20px] shrink-0'>
              <div className='flex items-center justify-center pl-0 pr-[10px] py-0 relative shrink-0'>
                {previewMvps.map((mvp) => (
                  <div
                    key={mvp.id}
                    className='border-[3px] border-grayscale-gray00 border-solid mr-[-10px] relative rounded-[50px] shrink-0 size-[50px]'
                  >
                    {renderSmallAvatar(mvp, 50)}
                  </div>
                ))}
                {remainingCount > 0 && (
                  <div className='bg-green-deep01 border-[3px] border-grayscale-gray00 border-solid flex flex-col items-center justify-center mr-[-10px] overflow-clip p-[10px] relative rounded-[50px] shrink-0 size-[50px]'>
                    <p
                      className={`${applyTypography('b12Strong')} text-text-inverse shrink-0 text-center w-full whitespace-pre-wrap`}
                      style={{ lineHeight: '1.4' }}
                    >
                      +{remainingCount}
                    </p>
                  </div>
                )}
              </div>
              <p
                className={`${applyTypography('b13Strong')} text-text-accent shrink-0 underline`}
                style={{ lineHeight: '1.4', textUnderlineOffset: '14%' }}
              >
                {t('See all mvp', 'MVP 전체 보기')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MVPSection;
